import { Warning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * Standard legal acknowledgment block used at every account/transaction
 * touchpoint (signup, vendor upgrade, purchase). The wording is deliberately
 * consistent everywhere so users can't say "I saw a different version".
 *
 * Props:
 *   variant  — "account" | "vendor" | "transaction"
 *   checked  — controlled state of the acknowledgment checkbox
 *   onChange — (bool) => void
 *   testId   — root data-testid (default legal-notice)
 */
const HEADLINES = {
  account: "Hunt57 Terms — please read before creating your account",
  vendor: "Vendor Terms — please read before becoming a seller",
  transaction: "Purchase Terms — please read before opening this Purchase Order",
};

const CHECKBOX_LABELS = {
  account: "I have read and agree to the Hunt57 Terms of Service and Community Guidelines above.",
  vendor: "I have read and agree to the Hunt57 Vendor Terms. As a seller I am solely responsible for my transactions.",
  transaction: "I have read and agree that Hunt57 is not a party to this sale and holds no responsibility for its outcome.",
};

export default function LegalNotice({ variant = "account", checked, onChange, testId = "legal-notice" }) {
  return (
    <div data-testid={testId} className="border border-[#FFD700]/40 bg-[#FFD700]/5 p-4">
      <div className="flex items-start gap-2 mb-3">
        <Warning size={16} weight="fill" className="text-[#FFD700] shrink-0 mt-0.5" />
        <h4 className="font-mono text-[11px] tracking-widest uppercase text-[#FFD700] leading-snug">
          {HEADLINES[variant]}
        </h4>
      </div>
      <ul className="space-y-2 text-[12px] leading-relaxed text-zinc-300 list-disc pl-5">
        <li>
          <strong className="text-white">Hunt57 is a listing service only.</strong> All card sales are strictly between the buyer and the seller. Hunt57 does not participate in, monitor, or facilitate any sale.
        </li>
        <li>
          <strong className="text-white">Hunt57 does not handle money.</strong> Payments happen off-platform via the seller&apos;s chosen path (PayPal, Cash App, Venmo). Hunt57 never touches funds, does not escrow, refund, or guarantee payments.
        </li>
        <li>
          <strong className="text-white">Disputes are between buyer and seller.</strong> Any dispute, chargeback, arbitration, or legal action arising from a transaction must exclude Hunt57. Hunt57 will not be joined as a party and accepts no liability for transaction outcomes.
        </li>
        <li>
          <strong className="text-white">You are responsible for your own compliance.</strong> This includes tax reporting, shipping laws, authenticity, and card-condition accuracy.
        </li>
        <li>
          Using Hunt57 constitutes acceptance of these terms. Full text at{" "}
          <Link to="/terms" target="_blank" rel="noreferrer" className="text-[#FFD700] underline underline-offset-2 hover:text-white" data-testid={`${testId}-terms-link`}>
            /terms
          </Link>{" "}
          and{" "}
          <Link to="/guidelines" target="_blank" rel="noreferrer" className="text-[#FFD700] underline underline-offset-2 hover:text-white" data-testid={`${testId}-guidelines-link`}>
            /guidelines
          </Link>.
        </li>
        {variant === "vendor" && (
          <li>
            <strong className="text-white">Vendor billing.</strong> A payment method is required on file to start the 30-day free trial. Cancel any time during the trial for no charge. If not canceled by day 30, a launch-promo first-year charge of $49.99 is applied (regular $69.99, $20 off first year). Renews annually at $69.99 on that same date. Founding Members are exempt while active and in good standing.
          </li>
        )}
      </ul>
      <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`${testId}-checkbox`}
          className="mt-0.5 w-4 h-4 accent-[#FF5F1F] shrink-0"
        />
        <span className="text-[12px] leading-snug text-zinc-200">{CHECKBOX_LABELS[variant]}</span>
      </label>
    </div>
  );
}
